import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("timeout"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (tries.current >= 8) { setState("timeout"); return; }
      tries.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          try {
            const cfg = await api.get("/billing/config");
            if (!cfg.data.onboarding_completed) { navigate("/admin/onboarding", { replace: true }); return; }
          } catch (e) { console.debug("billing config check failed:", e?.response?.status); }
          setState("paid"); return;
        }
      } catch (e) { console.debug("payment status poll:", e?.response?.status); }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center">
      {state === "checking" && (<><Loader2 className="h-10 w-10 animate-spin text-[#0F52BA]" /><p className="text-slate-500">Confirming your payment…</p></>)}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-green-500" />
          <h1 className="text-2xl font-bold text-slate-900">Subscription active</h1>
          <p className="max-w-sm text-slate-500">Your Nimbus portal is now active. Thank you!</p>
          <Button className="mt-2 bg-[#0F52BA] hover:bg-[#0B3D8A]" onClick={() => navigate("/admin")} data-testid="success-continue">Go to dashboard</Button>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="h-10 w-10 text-slate-400" />
          <h1 className="text-xl font-semibold text-slate-900">Still processing</h1>
          <p className="max-w-sm text-slate-500">Your payment is taking a moment to confirm. You can return to the dashboard; the status will update shortly.</p>
          <Button variant="outline" onClick={() => navigate("/admin")} data-testid="success-timeout-continue">Back to dashboard</Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center">
      <XCircle className="h-14 w-14 text-slate-400" />
      <h1 className="text-2xl font-bold text-slate-900">Checkout canceled</h1>
      <p className="max-w-sm text-slate-500">No charge was made. You can subscribe anytime from your settings.</p>
      <Button className="mt-2 bg-[#0F52BA] hover:bg-[#0B3D8A]" onClick={() => navigate("/admin/settings")} data-testid="cancel-back">Back to settings</Button>
    </div>
  );
}
